import React, { useMemo, useState, useEffect, useCallback } from 'react';

import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import KpiCard from '../../components/KpiCard';
import Header from '../../components/Header';
import SiparisOneriWidget from '../../components/SiparisOneriWidget';
import { styles, colors, radius, shadows } from '../../styles/theme';
import useCachedQuery from '../../hooks/useCachedQuery';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const StatusBadge = ({ status }) => {
  const style = styles.statusColors[status] || { bg: '#e9ecef', color: '#495057' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 'bold',
      background: style.bg,
      color: style.color
    }}>
      {status}
    </span>
  );
};

const SatinalmaDashboard = () => {
  const { user } = useAuth();

  const [message, setMessage] = useState('');
  const userCacheKey = user?.id || user?.email || user?.name || 'anon';

  const {
    data: talepler = [],
    isLoading: taleplerLoading,
    isValidating: taleplerValidating,
    mutate: mutateTalepler,
    revalidate: revalidateTalepler
  } = useCachedQuery(
    ['dashboard', userCacheKey, 'satinalma', 'talepler'],
    async () => {
      const res = await fetch(`${API_URL}/api/talepler`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    { staleTime: 20000 }
  );

  const {
    data: siparisler = [],
    isLoading: siparislerLoading,
    isValidating: siparislerValidating,
    revalidate: revalidateSiparişler
  } = useCachedQuery(
    ['dashboard', userCacheKey, 'satinalma', 'siparisler'],
    async () => {
      const res = await fetch(`${API_URL}/api/siparisler`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    { staleTime: 20000 }
  );

  const {
    data: tedarikciler = [],
    isLoading: tedarikcilerLoading,
    isValidating: tedarikcilerValidating,
    revalidate: revalidateTedarikçiler
  } = useCachedQuery(
    ['dashboard', userCacheKey, 'satinalma', 'tedarikciler'],
    async () => {
      const res = await fetch(`${API_URL}/api/tedarikciler`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    { staleTime: 20000 }
  );

  const loading = taleplerLoading || siparislerLoading || tedarikcilerLoading;
  const isValidating = taleplerValidating || siparislerValidating || tedarikcilerValidating;

  // Auto-refresh her 30 saniye
  const refreshAll = useCallback(() => {
    revalidateTalepler({ force: true }).catch(() => {});
    revalidateSiparişler({ force: true }).catch(() => {});
    revalidateTedarikçiler({ force: true }).catch(() => {});
  }, [revalidateTalepler, revalidateSiparişler, revalidateTedarikçiler]);

  useEffect(() => {
    const interval = setInterval(refreshAll, 30000);
    return () => clearInterval(interval);
  }, [refreshAll]);

  const handleApproveTalep = async (talepId) => {
    const previousData = mutateTalepler(prev => {
      const list = Array.isArray(prev) ? prev : [];
      return list.map(t => (t.id === talepId ? { ...t, durum: 'ONAYLANDI' } : t));
    }, { revalidate: false });

    setMessage('Talep onaylandı!');
    setTimeout(() => setMessage(''), 3000);

    try {
      const res = await fetch(`${API_URL}/api/talepler/${talepId}/onayla`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await revalidateTalepler({ force: true });
    } catch (err) {
      mutateTalepler(previousData, { revalidate: false });
      setMessage('Talep onaylanamadi.');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const haftalikHarcama = useMemo(() => siparisler
    .filter(s => {
      const siparisTarihi = new Date(s.siparis_tarihi);
      const bugun = new Date();
      const yediGunOnce = new Date();
      yediGunOnce.setDate(bugun.getDate() - 7);
      return siparisTarihi >= yediGunOnce;
    })
    .reduce((total, s) => total + (s.genel_toplam || 0), 0), [siparisler]);

  return (
    <div>
      <Header title="Satinalma Panosu" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>
        {message && (
          <div style={{ background: '#d4edda', padding: '12px 20px', marginBottom: '20px', borderRadius: '6px', color: '#155724', borderLeft: `4px solid ${styles.success}` }}>
            {message}
          </div>
        )}

        {user && hasPermission(user.role, PERMISSIONS.CREATE_REQUEST) && (
          <SiparisOneriWidget user={user} />
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px', minHeight: '20px' }}>
          {isValidating && !loading && (
            <span style={{ fontSize: '12px', color: colors.textSecondary, background: colors.backgroundAlt, borderRadius: '12px', padding: '4px 10px' }}>
              Güncelleniyor...
            </span>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          <KpiCard title="Bekleyen Talepler" value={loading ? '...' : talepler.filter(t => t.durum === 'BEKLENIYOR').length} color={styles.warning} icon="clipboard" />
          <KpiCard title="Aktif Siparişler" value={loading ? '...' : siparisler.length} color={styles.info} icon="package" />
          {hasPermission(user.role, PERMISSIONS.VIEW_FINANCIAL_FIYAT) && (
            <KpiCard title="Haftalık Harcama" value={loading ? '...' : `${haftalikHarcama.toLocaleString('tr-TR', { minimumFractionDigits: 2, style: 'currency', currency: 'TRY' })}`} color={styles.primary} icon="money" />
          )}
          <KpiCard title="Tedarikçi Sayısı" value={loading ? '...' : tedarikciler.length} color={styles.success} icon="factory" />
        </div>

        {/* Hızlı İşlemler — Card Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          marginBottom: '24px'
        }}>
          {[
            { label: 'Yeni Talep', icon: '📋', route: '/satinalma#talepler', color: '#1565c0' },
            { label: 'Yeni Sipariş', icon: '🛒', route: '/satinalma#siparisler', color: '#e65100' },
            { label: 'Onay Bekleyenler', icon: '⏳', route: '/satinalma#onaylar', color: '#2e7d32' },
            { label: 'Tedarikçiler', icon: '🏭', route: '/tedarikciler-grup', color: '#7b1fa2' },
            { label: 'Stok Durumu', icon: '📦', route: '/stok-depo#stok-durumu', color: '#00838f' },
          ].map(item => (
            <div
              key={item.label}
              onClick={() => { window.location.href = item.route; }}
              style={{
                background: colors.surface, borderRadius: radius.xl, padding: '20px 16px',
                textAlign: 'center', cursor: 'pointer', borderLeft: `4px solid ${item.color}`,
                boxShadow: shadows.sm, transition: 'transform 0.15s, box-shadow 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = shadows.sm; }}
            >
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>{item.icon}</div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: item.color }}>{item.label}</div>
            </div>
          ))}
        </div>

        <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '20px' }}>
          <h3 style={{ margin: '0 0 15px 0', color: colors.textPrimary, fontSize: '15px', fontWeight: 600, letterSpacing: '-0.01em' }}>Son Talepler</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: colors.backgroundAlt }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Talep No</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Sube</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Malzeme</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Miktar</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Durum</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Islem</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, row) => (
                  <tr key={`skeleton-${row}`} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    {Array.from({ length: 6 }).map((__, col) => (
                      <td key={`skeleton-${row}-${col}`} style={{ padding: '12px 16px' }}>
                        <div style={{ height: '12px', borderRadius: '4px', background: colors.border, width: col === 1 ? '85%' : '70%' }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : talepler.slice(0, 5).map(talep => (
                <tr key={talep.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <td style={{ padding: '12px 16px', fontWeight: 'bold' }}>{talep.talep_no}</td>
                  <td style={{ padding: '12px 16px' }}>{talep.sube}</td>
                  <td style={{ padding: '12px 16px' }}>{talep.malzeme}</td>
                  <td style={{ padding: '12px 16px' }}>{talep.miktar} {talep.birim}</td>
                  <td style={{ padding: '12px 16px' }}><StatusBadge status={talep.durum} /></td>
                  <td style={{ padding: '12px 16px' }}>
                    {talep.durum === 'BEKLENIYOR' && (
                      <button
                        onClick={() => handleApproveTalep(talep.id)}
                        style={{ padding: '5px 10px', background: styles.success, color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                      >
                        Onayla
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SatinalmaDashboard;
